/**
 * R7 customer feedback page (feedback.fixitninjaz.com).
 *
 * Public, unauthenticated. Token = job ULID embedded in the URL:
 *   https://feedback.fixitninjaz.com/r/<jobId>
 *
 * Two equal CTAs are shown to every customer per §20 R7:
 *   1. Leave a Google review — opens the public review URL.
 *   2. Send private feedback — inline 1–5 star + comment form.
 *
 * EN + AR with a quick locale toggle. Default is browser language; RTL flips
 * via `dir="rtl"` on <html>.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { SERVICE_LABELS, type ServiceType } from '@fin/shared/enums';
import type { FeedbackSource } from '@fin/shared/schemas';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const GOOGLE_REVIEW_URL =
  (import.meta.env.VITE_GOOGLE_REVIEW_URL as string | undefined) ??
  'https://g.page/r/fixitninjaz/review';

type Locale = 'en' | 'ar';

type StringKey =
  | 'header'
  | 'sub'
  | 'google'
  | 'googleSub'
  | 'private'
  | 'privateSub'
  | 'rating'
  | 'comment'
  | 'contact'
  | 'submit'
  | 'sending'
  | 'thanks'
  | 'googleThanks'
  | 'notFound'
  | 'unavailable'
  | 'error'
  | 'job'
  | 'worker'
  | 'back';

const STR: Record<Locale, Record<StringKey, string>> = {
  en: {
    header: 'How was your service?',
    sub: 'We hope your job went smoothly. Choose how you would like to share your feedback.',
    google: 'Leave a Google review',
    googleSub: 'Helps other customers find us',
    private: 'Send private feedback',
    privateSub: 'Goes straight to our team',
    rating: 'How would you rate the service?',
    comment: 'Anything we should know?',
    contact: 'Phone or email (optional, for follow-up)',
    submit: 'Send feedback',
    sending: 'Sending…',
    thanks: 'Thank you — we received your feedback.',
    googleThanks: 'Thanks! Opening Google reviews…',
    notFound: 'We could not find that job. Please check the link.',
    unavailable: 'Feedback is only available once your job is in progress or completed.',
    error: 'Something went wrong. Please try again.',
    job: 'Job',
    worker: 'Technician',
    back: 'Back',
  },
  ar: {
    header: 'كيف كانت الخدمة؟',
    sub: 'نأمل أن تكون الخدمة قد تمت بسلاسة. اختر كيف تود مشاركة تعليقاتك.',
    google: 'اترك تقييماً على Google',
    googleSub: 'يساعد عملاء آخرين في العثور علينا',
    private: 'أرسل ملاحظات خاصة',
    privateSub: 'تصل مباشرة إلى فريقنا',
    rating: 'كيف تقيّم الخدمة؟',
    comment: 'هل من شيء نود معرفته؟',
    contact: 'رقم الهاتف أو البريد (اختياري، للمتابعة)',
    submit: 'أرسل الملاحظات',
    sending: 'جارٍ الإرسال…',
    thanks: 'شكراً — تم استلام ملاحظاتك.',
    googleThanks: 'شكراً! يتم فتح تقييمات Google…',
    notFound: 'لم نعثر على هذا الطلب. يرجى التحقق من الرابط.',
    unavailable: 'يمكن إرسال الملاحظات فقط بعد بدء الخدمة أو إتمامها.',
    error: 'حدث خطأ ما. حاول مرة أخرى.',
    job: 'الطلب',
    worker: 'الفني',
    back: 'رجوع',
  },
};

interface PublicJob {
  id: string;
  serviceType: ServiceType;
  status: string;
  scheduledAt: string;
  workerName?: string;
}

function detectLocale(): Locale {
  if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('ar')) {
    return 'ar';
  }
  return 'en';
}

function useLocale(): [Locale, (l: Locale) => void] {
  const [locale, setLocale] = useState<Locale>(detectLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);
  return [locale, setLocale];
}

function Stars({ value, onChange }: { value: number; onChange: (v: number) => void }): JSX.Element {
  return (
    <div className="flex gap-2 justify-center" role="radiogroup">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} stars`}
          onClick={() => onChange(n)}
          className="text-4xl leading-none focus:outline-none focus:ring-2 focus:ring-offset-2 rounded"
          style={{ color: n <= value ? BRAND.accent : '#D6D3D1' }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function FeedbackPage(): JSX.Element {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const [locale, setLocale] = useLocale();
  const t = useMemo(() => STR[locale], [locale]);

  const [job, setJob] = useState<PublicJob | null>(null);
  const [loadError, setLoadError] = useState<'notfound' | 'unavailable' | 'error' | null>(null);
  const [view, setView] = useState<'choose' | 'private' | 'thanks-google' | 'thanks-private'>('choose');

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_URL}/public/jobs/${jobId}`);
        if (cancelled) return;
        if (res.status === 404) {
          setLoadError('notfound');
          return;
        }
        if (!res.ok) {
          setLoadError('error');
          return;
        }
        const data = (await res.json()) as PublicJob;
        const ALLOWED = new Set(['in_progress', 'completed']);
        if (!ALLOWED.has(data.status)) {
          setLoadError('unavailable');
          return;
        }
        setJob(data);
      } catch {
        if (!cancelled) setLoadError('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  async function submit(source: FeedbackSource, body: Record<string, unknown>): Promise<boolean> {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, source, ...body }),
      });
      if (!res.ok) {
        setSubmitError(t.error);
        return false;
      }
      return true;
    } catch {
      setSubmitError(t.error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function chooseGoogle(): Promise<void> {
    await submit('google', {});
    setView('thanks-google');
    setTimeout(() => {
      window.location.href = GOOGLE_REVIEW_URL;
    }, 900);
  }

  async function submitPrivate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (rating < 1) return;
    const ok = await submit('private', {
      rating,
      comment: comment.trim() || undefined,
      contact: contact.trim() || undefined,
    });
    if (ok) setView('thanks-private');
  }

  return (
    <main className="min-h-screen flex flex-col p-6 bg-stone-50">
      <header className="flex justify-between items-center max-w-md w-full mx-auto mb-6">
        <span className="font-semibold" style={{ color: BRAND.primary }}>
          {BRAND.name}
        </span>
        <button
          type="button"
          onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
          className="text-sm underline text-stone-600"
        >
          {locale === 'en' ? 'العربية' : 'English'}
        </button>
      </header>

      <section className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-sm p-6 border border-stone-200">
        {loadError === 'notfound' ? (
          <p className="text-stone-700">{t.notFound}</p>
        ) : loadError === 'unavailable' ? (
          <p className="text-stone-700">{t.unavailable}</p>
        ) : loadError === 'error' ? (
          <p className="text-stone-700">{t.error}</p>
        ) : view === 'thanks-google' ? (
          <p className="text-center text-lg" style={{ color: BRAND.primary }}>
            {t.googleThanks}
          </p>
        ) : view === 'thanks-private' ? (
          <p className="text-center text-lg" style={{ color: BRAND.primary }}>
            {t.thanks}
          </p>
        ) : view === 'private' ? (
          <form onSubmit={submitPrivate} className="space-y-4">
            <button
              type="button"
              onClick={() => setView('choose')}
              className="text-sm text-stone-500 underline"
            >
              ← {t.back}
            </button>
            <div>
              <label className="block text-sm font-medium mb-2">{t.rating}</label>
              <Stars value={rating} onChange={setRating} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="comment">
                {t.comment}
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                className="w-full rounded-md border border-stone-300 p-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="contact">
                {t.contact}
              </label>
              <input
                id="contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                maxLength={160}
                className="w-full rounded-md border border-stone-300 p-2 text-sm focus:outline-none focus:ring-2"
              />
            </div>
            {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
            <button
              type="submit"
              disabled={submitting || rating < 1}
              className="w-full rounded-md py-3 text-white font-medium disabled:opacity-60"
              style={{ backgroundColor: BRAND.primary }}
            >
              {submitting ? t.sending : t.submit}
            </button>
          </form>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-center mb-2" style={{ color: BRAND.primary }}>
              {t.header}
            </h1>
            <p className="text-stone-600 text-center mb-4">{t.sub}</p>
            {job ? (
              <p className="text-sm text-center text-stone-500 mb-6">
                {t.job}: {SERVICE_LABELS[job.serviceType]?.[locale]}
                {job.workerName ? ` · ${t.worker}: ${job.workerName}` : ''}
              </p>
            ) : null}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => void chooseGoogle()}
                disabled={submitting}
                className="w-full rounded-xl p-4 text-left border border-stone-200 hover:border-stone-300 disabled:opacity-60"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="font-semibold" style={{ color: BRAND.primary }}>
                  {t.google}
                </div>
                <div className="text-sm text-stone-500">{t.googleSub}</div>
              </button>
              <button
                type="button"
                onClick={() => setView('private')}
                className="w-full rounded-xl p-4 text-left border border-stone-200 hover:border-stone-300"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="font-semibold" style={{ color: BRAND.primary }}>
                  {t.private}
                </div>
                <div className="text-sm text-stone-500">{t.privateSub}</div>
              </button>
            </div>
          </>
        )}
      </section>

      <footer className="text-xs text-stone-400 text-center mt-6">
        © {new Date().getFullYear()} {BRAND.name}
      </footer>
    </main>
  );
}

function NotFound(): JSX.Element {
  const [locale] = useLocale();
  const t = STR[locale];
  return (
    <main className="min-h-screen flex items-center justify-center p-6">
      <p className="text-stone-700">{t.notFound}</p>
    </main>
  );
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/r/:jobId" element={<FeedbackPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
